import { CheckCircle, AlertCircle, XCircle } from 'lucide-react';

type HealthStatus = 'healthy' | 'warning' | 'error';

interface HealthBadgeProps {
  score: number;
  status: HealthStatus;
  showScore?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function HealthBadge({ score, status, showScore = true, size = 'md' }: HealthBadgeProps) {
  const config = {
    healthy: {
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-100',
      ring: 'ring-green-500',
    },
    warning: {
      icon: AlertCircle,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100',
      ring: 'ring-yellow-500',
    },
    error: {
      icon: XCircle,
      color: 'text-red-600',
      bg: 'bg-red-100',
      ring: 'ring-red-500',
    },
  };

  const sizeConfig = {
    sm: { circleSize: 32, iconSize: 14, fontSize: 'text-xs', strokeWidth: 3 },
    md: { circleSize: 48, iconSize: 18, fontSize: 'text-sm', strokeWidth: 4 },
    lg: { circleSize: 64, iconSize: 24, fontSize: 'text-lg', strokeWidth: 5 },
  };

  const { icon: Icon, color, bg } = config[status];
  const { circleSize, fontSize, strokeWidth } = sizeConfig[size];

  const circumference = 2 * Math.PI * ((circleSize - strokeWidth) / 2);
  const offset = circumference - (score / 100) * circumference;

  if (!showScore) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${bg} ${color}`}>
        <Icon size={12} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  }

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg
        width={circleSize}
        height={circleSize}
        className="transform -rotate-90"
      >
        <circle
          cx={circleSize / 2}
          cy={circleSize / 2}
          r={(circleSize - strokeWidth) / 2}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200"
        />
        <circle
          cx={circleSize / 2}
          cy={circleSize / 2}
          r={(circleSize - strokeWidth) / 2}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={color}
        />
      </svg>
      <span className={`absolute ${fontSize} font-semibold ${color}`}>
        {score}
      </span>
    </div>
  );
}

export function HealthScoreCircle({
  score,
  size = 64,
  strokeWidth = 4,
}: {
  score: number;
  size?: number;
  strokeWidth?: number;
}) {
  const status: HealthStatus = score >= 80 ? 'healthy' : score >= 60 ? 'warning' : 'error';
  const colorClass = status === 'healthy' ? 'text-green-500' : status === 'warning' ? 'text-yellow-500' : 'text-red-500';

  const circumference = 2 * Math.PI * ((size - strokeWidth) / 2);
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={(size - strokeWidth) / 2}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={(size - strokeWidth) / 2}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={colorClass}
        />
      </svg>
      <span className={`absolute text-lg font-bold ${colorClass}`}>{score}</span>
    </div>
  );
}
